import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { type FormikHelpers, useFormikContext } from 'formik'
import * as React from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { errorMessage } from 'src/helpers/fns'
import { useAdminAccess, useApi, useDebouncedLoadOptions } from 'src/helpers/hooks'
import { type AuthenticatedHeaders, useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import { Button } from 'src/tailwind/components/Button'
import AsyncSelect from 'src/views/components/AsyncSelect'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError, GeneralFormError } from 'src/views/components/forms/formik/FormError'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import Loader from 'src/views/components/Loader'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import Swal from 'sweetalert2'
import { GRID_TWO } from '../../programs/AdministratorProgramPage/constants'
import { FormSection } from '../../programs/AdministratorProgramPage/FormSection'

// ─── Form types ───────────────────────────────────────────────────────────────

interface GroupFormValues {
  readonly name: string
  readonly quotaMin: string
  readonly quotaMax: string
  readonly learningDuration: string
  readonly examDuration: string
  readonly allowInSems: readonly number[]
  readonly lecturerIds: readonly number[]
}

interface LecturerOption {
  readonly value: number
  readonly label: string
}

const EMPTY_VALUES: GroupFormValues = {
  name: 'ჯგუფი ',
  quotaMin: '0',
  quotaMax: '',
  learningDuration: '',
  examDuration: '',
  allowInSems: [],
  lecturerIds: [],
}

const ALLOW_IN_SEMS = Array.from({ length: 30 }, (_, i) => i + 1)

// ─── SemesterChipGrid – mirrors SemesterGrid from AdministratorFacultyPage ───

const SemChip = React.memo(function SemChip({
  semester,
  checked,
  onToggle,
}: {
  readonly semester: number
  readonly checked: boolean
  readonly onToggle: () => void
}): JSX.Element {
  const id = `group-sem-${semester}-${React.useId()}`
  return (
    <label
      htmlFor={id}
      className={classNames(
        'relative inline-flex cursor-pointer items-center justify-center rounded border p-1.5 transition-colors',
        checked
          ? 'border-primaryBlue bg-primaryBlue/10 text-primaryBlue'
          : 'border-borderGrey bg-card hover:border-primaryBlue/50 hover:bg-gray-50 dark:hover:bg-gray-800'
      )}
    >
      <input type="checkbox" id={id} checked={checked} onChange={onToggle} className="peer sr-only" />
      <span className="text-xs font-medium text-primaryTextColor">{semester}</span>
    </label>
  )
})

function SemesterChipGrid(): JSX.Element {
  const formik = useFormikContext<GroupFormValues>()
  const t = useTranslatable()
  const value = formik.values.allowInSems

  const selectAll = React.useCallback(() => {
    void formik.setFieldValue('allowInSems', [...ALLOW_IN_SEMS])
  }, [formik])

  const deselectAll = React.useCallback(() => {
    void formik.setFieldValue('allowInSems', [])
  }, [formik])

  const toggle = React.useCallback(
    (sem: number) => {
      const next = value.includes(sem) ? value.filter((s) => s !== sem) : [...value, sem].sort((a, b) => a - b)
      void formik.setFieldValue('allowInSems', next)
    },
    [formik, value]
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end gap-2">
        <button type="button" onClick={selectAll} className="text-xs text-primaryBlueLink hover:underline">
          {t('common:select_all')}
        </button>
        <span className="text-captionColor">|</span>
        <button type="button" onClick={deselectAll} className="text-xs text-primaryBlueLink hover:underline">
          {t('common:deselect_all')}
        </button>
      </div>
      <div className="rounded-md border border-borderGrey bg-card p-3">
        <div className="sm:grid-cols-15 grid grid-cols-10 gap-2">
          {ALLOW_IN_SEMS.map((sem) => (
            <SemChip key={sem} semester={sem} checked={value.includes(sem)} onToggle={() => toggle(sem)} />
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Lecturer multiselect – uses formikContext like other field components ────

function LecturerMultiSelectField({
  headers,
  initialLecturerLabels,
}: {
  readonly headers: AuthenticatedHeaders
  readonly initialLecturerLabels: Readonly<Record<number, string>>
}): JSX.Element {
  const formik = useFormikContext<GroupFormValues>()
  const t = useTranslatable()
  const [pickerValue, setPickerValue] = React.useState<LecturerOption | null>(null)
  const [knownLabels, setKnownLabels] = React.useState<Readonly<Record<number, string>>>(initialLecturerLabels)

  React.useEffect(() => {
    setKnownLabels((prev) => {
      let changed = false
      const next = { ...prev }
      for (const [k, v] of Object.entries(initialLecturerLabels)) {
        const key = Number(k)
        if (next[key] !== v) {
          next[key] = v
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [initialLecturerLabels])

  const selectedLecturers = React.useMemo(
    () =>
      formik.values.lecturerIds.map((id) => ({
        value: id,
        label: knownLabels[id] ?? String(id),
      })),
    [knownLabels, formik.values.lecturerIds]
  )

  const loadOptions = useDebouncedLoadOptions<LecturerOption>(
    React.useCallback(
      async (q: string) => {
        const result = await Api.getAdministratorListsLecturersPersnums({
          headers,
          ...(q.length > 0 ? { query: { q } } : {}),
        })
        return result.data.map((row) => {
          const name = row.name.trim().length > 0 ? row.name.trim() : String(row.id)
          const pn = row.persNum?.trim() ?? ''
          const label = pn.length > 0 ? `${name} - ${pn}` : name
          return { value: row.id, label }
        })
      },
      [headers]
    )
  )

  const handleSelect = React.useCallback(
    (option: LecturerOption | null) => {
      if (option == null) return
      void formik.setFieldValue('lecturerIds', [...formik.values.lecturerIds, option.value])
      setKnownLabels((prev) => ({ ...prev, [option.value]: option.label }))
      setPickerValue(null)
    },
    [formik]
  )

  const handleRemove = React.useCallback(
    (id: number) => {
      void formik.setFieldValue(
        'lecturerIds',
        formik.values.lecturerIds.filter((lid) => lid !== id)
      )
    },
    [formik]
  )

  return (
    <div>
      <AsyncSelect<LecturerOption>
        loadOptions={loadOptions}
        value={pickerValue}
        onChange={handleSelect}
        placeholder={t(['ლექტორის ძიება...', 'Search lecturer...'])}
        isClearable
        cacheOptions
        defaultOptions
        isDisabled={formik.isSubmitting}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        menuPosition="fixed"
      />
      {selectedLecturers.length > 0 && (
        <ul className="mt-3 divide-y divide-borderGrey rounded-lg border border-borderGrey bg-primaryWhite">
          {selectedLecturers.map((lecturer) => (
            <li key={lecturer.value} className="flex items-center justify-between gap-3 px-3 py-2.5">
              <span className="min-w-0 text-sm text-primaryTextColor">{lecturer.label}</span>
              <button
                type="button"
                className="inline-flex shrink-0 items-center justify-center rounded-md border border-borderGrey p-2 text-captionColor transition-colors hover:border-primaryRed hover:bg-red-50 hover:text-primaryRed"
                onClick={() => handleRemove(lecturer.value)}
                aria-label={t('common:remove')}
                disabled={formik.isSubmitting}
              >
                <FontAwesomeIcon icon={ProRegularSvgIcons.faTrashCan} className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdministratorCourseGroupsPage(): JSX.Element {
  const t = useTranslatable()
  return (
    <SuspenseWrapper title={t('subPage:groups')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const canManageCourses = useAdminAccess().canManage('courses')
  const headers = useAuthenticatedHeaders()
  const { courseId } = useParams()
  const [searchParams] = useSearchParams()
  const activeSemester = useActiveSemester()
  const semIdStr = searchParams.get('semId') ?? (activeSemester?.id != null ? String(activeSemester.id) : '')
  const parsedSemId = semIdStr !== '' ? parseInt(semIdStr, 10) : null

  const [editingGroup, setEditingGroup] = React.useState<Api.AdministratorCourseGroupItem | null>(null)
  const [deleteGroupError, setDeleteGroupError] = React.useState<string | null>(null)

  // Fetch course to get degree (required by duration options endpoint)
  const { data: course } = useApi({
    endpoint: Api.getAdministratorCourse,
    params: React.useMemo(
      () => ({ headers, args: { id: courseId! }, query: { semId: parsedSemId ?? 0 } }),
      [courseId, headers, parsedSemId]
    ),
    shouldFetch: courseId != null && parsedSemId != null,
    suspense: false,
  })

  const degree = course?.degree?.value ?? null

  const { data: durationOptions, isLoading: isDurationsLoading } = useApi({
    endpoint: Api.getAdministratorListsGroupDurations,
    params: React.useMemo(
      () => ({ headers, query: { semId: parsedSemId ?? 0, degree: degree ?? 0 } }),
      [degree, headers, parsedSemId]
    ),
    shouldFetch: parsedSemId != null && degree != null,
  })

  const learningDurationOptions = React.useMemo(
    () => [
      { value: '', label: t('common:select') },
      ...(durationOptions?.learningDurations ?? []).map((n: number) => ({ value: String(n), label: String(n) })),
    ],
    [durationOptions, t]
  )

  const examDurationOptions = React.useMemo(
    () => [
      { value: '', label: t('common:select') },
      ...(durationOptions?.examDurations ?? []).map((n: number) => ({ value: String(n), label: String(n) })),
    ],
    [durationOptions, t]
  )

  const {
    data: groups,
    mutate,
    isLoading: isGroupsLoading,
  } = useApi({
    endpoint: Api.getAdministratorCourseGroups,
    params: React.useMemo(
      () => ({
        headers,
        args: { id: courseId! },
        query: parsedSemId != null ? { semId: parsedSemId } : {},
      }),
      [courseId, headers, parsedSemId]
    ),
    shouldFetch: courseId != null,
  })

  const groupLecturerLabels = React.useMemo(() => {
    const labels: Record<number, string> = {}
    for (const group of groups ?? []) {
      for (const l of group.lecturers ?? []) {
        const id = parseInt(l.id, 10)
        if (labels[id] == null) {
          const name = l.fullName.trim().length > 0 ? l.fullName.trim() : l.id
          const pn = l.personalNumber?.trim() ?? ''
          labels[id] = pn.length > 0 ? `${name} - ${pn}` : name
        }
      }
    }
    return labels
  }, [groups])

  const initialValues = React.useMemo((): GroupFormValues => {
    if (editingGroup == null) {
      const lastLearning = durationOptions?.learningDurations.at(-1)
      const lastExam = durationOptions?.examDurations.at(-1)
      return {
        ...EMPTY_VALUES,
        learningDuration: lastLearning != null ? String(lastLearning) : '',
        examDuration: lastExam != null ? String(lastExam) : '',
      }
    }
    return {
      name: editingGroup.name,
      quotaMin: String(editingGroup.quotaMin),
      quotaMax: String(editingGroup.quotaMax),
      learningDuration: String(editingGroup.learningDuration),
      examDuration: String(editingGroup.examDuration),
      allowInSems: editingGroup.allowInSems ?? [],
      lecturerIds: (editingGroup.lecturers ?? []).map((l) => parseInt(l.id, 10)),
    }
  }, [durationOptions, editingGroup])

  const handleSubmit = React.useCallback(
    async (values: GroupFormValues, helpers: FormikHelpers<GroupFormValues>) => {
      try {
        const body = {
          name: values.name,
          quotaMin: parseInt(values.quotaMin, 10),
          quotaMax: parseInt(values.quotaMax, 10),
          learningDuration: parseInt(values.learningDuration, 10),
          examDuration: parseInt(values.examDuration, 10),
          lecturerIds: [...values.lecturerIds],
          ...(parsedSemId != null && { semId: parsedSemId }),
          allowInSems: [...values.allowInSems],
        }
        if (editingGroup != null) {
          await Api.putAdministratorCourseGroup({
            headers,
            args: { id: courseId!, groupId: parseInt(editingGroup.id, 10) },
            body,
          })
        } else {
          await Api.postAdministratorCourseGroups({
            headers,
            args: { id: courseId! },
            body,
          })
        }
        await mutate()
        setEditingGroup(null)
        helpers.resetForm()
        toast.success(t('common:updated_successfully'))
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
        throw error
      }
    },
    [courseId, editingGroup, headers, mutate, parsedSemId, t]
  )

  const handleDeleteGroup = React.useCallback(
    async (group: Api.AdministratorCourseGroupItem) => {
      const alert = await Swal.fire({
        title: t('common:are_you_sure'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('common:confirm'),
        cancelButtonText: t('common:cancel'),
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6C757D',
      })
      if (!alert.isConfirmed) return
      setDeleteGroupError(null)
      try {
        await Api.deleteAdministratorCourseGroup({
          headers,
          args: { id: courseId!, groupId: parseInt(group.id, 10) },
          query: parsedSemId != null ? { semId: parsedSemId } : {},
        })
        await mutate()
        if (editingGroup?.id === group.id) setEditingGroup(null)
        toast.success(t(['წარმატებით წაიშალა', 'Successfully deleted']))
      } catch (error) {
        const message = errorMessage(error as base.CommonError)
        setDeleteGroupError(message)
        toast.error(message)
      }
    },
    [courseId, editingGroup?.id, headers, mutate, parsedSemId, t]
  )

  if (courseId == null) return <Loader className="m-auto mt-8 flex" />

  const groupsList = groups ?? []
  const isEditing = editingGroup != null

  return (
    <div className="mt-4 w-full space-y-6">
      {/* Form card */}
      <Form initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
        <fieldset disabled={!canManageCourses} className="min-w-0 rounded-card bg-card px-6 py-8">
          <FormError />
          <div className="space-y-5">
            <TextInput required name="name" type="text" label={t('portfolio:description')} />

            <div className={GRID_TWO}>
              <TextInput required name="quotaMin" type="number" label={t(['ქვოტა მინ.', 'Quota Min'])} />
              <TextInput required name="quotaMax" type="number" label={t(['ქვოტა მაქს.', 'Quota Max'])} />
            </div>

            <div className={GRID_TWO}>
              <SelectInput
                required
                name="learningDuration"
                label={t(['სასწავლო ხანგრძლივობა', 'Learning Duration'])}
                options={learningDurationOptions}
                storeValueOnly
                disabled={isDurationsLoading}
                isLoading={isDurationsLoading}
              />
              <SelectInput
                required
                name="examDuration"
                label={t(['საგამოცდო ხანგრძლივობა', 'Exam Duration'])}
                options={examDurationOptions}
                storeValueOnly
                disabled={isDurationsLoading}
                isLoading={isDurationsLoading}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-primaryTextColor">
                {t(['ასარჩევად გამოუჩნდეს სემესტრელებს', 'Show For Semester Students To Select'])}
              </label>
              <SemesterChipGrid />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-primaryTextColor">
                {t('lecturer:lecturers')}
              </label>
              <LecturerMultiSelectField headers={headers} initialLecturerLabels={groupLecturerLabels} />
            </div>

            {canManageCourses && (
              <div className="flex flex-col gap-3 border-t border-borderGrey pt-5 sm:flex-row sm:justify-end">
                {isEditing && (
                  <Button
                    variant="outline"
                    type="button"
                    className="w-full sm:w-auto sm:min-w-[120px]"
                    onClick={() => setEditingGroup(null)}
                  >
                    {t('common:cancel')}
                  </Button>
                )}
                <Button variant="blue" type="submit" className="w-full sm:w-auto sm:min-w-[200px]">
                  {isEditing ? t('common:save') : t(['ჯგუფის დამატება', 'Add Group'])}
                </Button>
              </div>
            )}
          </div>
        </fieldset>
      </Form>

      {/* Groups list card */}
      <fieldset className="min-w-0 rounded-card bg-card px-6 py-8">
        <FormSection title={t('subPage:groups')}>
          {deleteGroupError != null ? <GeneralFormError messages={[deleteGroupError]} /> : null}
          {isGroupsLoading ? (
            <Loader className="m-auto flex py-8" />
          ) : groupsList.length === 0 ? (
            <p className="rounded-lg border border-dashed border-borderGrey bg-primaryWhite py-10 text-center text-caption text-captionColor">
              {t(['ჯგუფები ჯერ არ არის დამატებული', 'No groups have been added yet.'])}
            </p>
          ) : (
            <ul className="w-full space-y-3">
              {groupsList.map((group) => (
                <GroupCard
                  key={group.id}
                  group={group}
                  scheduleHref={`../schedule?groupId=${group.id}${parsedSemId != null ? `&semId=${parsedSemId}` : ''}`}
                  isSelected={editingGroup?.id === group.id}
                  onSelect={() => {
                    setEditingGroup(group)
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }}
                  onDelete={() => void handleDeleteGroup(group)}
                />
              ))}
            </ul>
          )}
        </FormSection>
      </fieldset>
    </div>
  )
}

// ─── Group card ───────────────────────────────────────────────────────────────

function GroupCard({
  group,
  scheduleHref,
  isSelected,
  onSelect,
  onDelete,
}: {
  readonly group: Api.AdministratorCourseGroupItem
  readonly scheduleHref: string
  readonly isSelected: boolean
  readonly onSelect: () => void
  readonly onDelete: () => void
}): JSX.Element {
  const t = useTranslatable()
  const canManageCourses = useAdminAccess().canManage('courses')

  return (
    <li
      className={classNames(
        'relative flex w-full flex-col gap-3 rounded-lg border bg-primaryWhite p-4 transition-colors',
        isSelected ? 'border-primaryBlue ring-1 ring-primaryBlue/30' : 'border-borderGrey'
      )}
    >
      <div className="absolute right-3 top-3 flex items-center gap-1.5">
        <Link
          to={scheduleHref}
          className="inline-flex items-center justify-center rounded-md border border-borderGrey p-2.5 text-captionColor transition-colors hover:border-primaryBlue hover:bg-blue-50 hover:text-primaryBlue"
          aria-label={t('common:schedule')}
        >
          <FontAwesomeIcon icon={ProRegularSvgIcons.faCalendarDays} className="size-4" />
        </Link>
        {canManageCourses && (
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md border border-borderGrey p-2.5 text-captionColor transition-colors hover:border-primaryRed hover:bg-red-50 hover:text-primaryRed"
            onClick={onDelete}
            aria-label={t('common:delete')}
          >
            <FontAwesomeIcon icon={ProRegularSvgIcons.faTrashCan} className="size-4" />
          </button>
        )}
      </div>

      <button type="button" className="min-w-0 flex-1 cursor-pointer pr-24 text-left" onClick={onSelect}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-primaryTextColor">
            {group.name} - ({group.quotaMin}–{group.quotaMax}) - {group.id}
          </span>
          {isSelected && (
            <span className="rounded-full bg-primaryBlue/10 px-2 py-0.5 text-xs font-medium text-primaryBlue">
              {t(['რედაქტირება', 'Editing'])}
            </span>
          )}
        </div>

        {group.lecturers != null && group.lecturers.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {group.lecturers.map((lecturer) => (
              <span
                key={lecturer.id}
                className="rounded-full border border-borderGrey px-2 py-0.5 text-xs text-primaryTextColor"
              >
                {lecturer.fullName}
              </span>
            ))}
          </div>
        )}
      </button>
    </li>
  )
}
